import { Injectable } from '@angular/core';

export interface RecentSearch {
  id: string;
  type: 'name' | 'mpi' | 'identifier' | 'teammate' | 'teammate_username' | 'teammate_facility' | 'provider' | 'facilityNumber' | 'careteamlookup' | 'appointment_mpi' | 'appointment_facility' | 'appointment_eventId' | 'appointment_timerange';
  query: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  identifierType?: string;
  timestamp: number;
  resultCount?: number;
  patientName?: string; // For MPI searches, store the patient name
  mpi?: string;
  teammateName?: string; // For teammate searches
  providerName?: string; // For provider searches
  excludeInactivePatients?: string;
  facilityNumber?: string;
  startDate?: string; // For appointment searches
  endDate?: string; // For appointment searches
  timeRangeStartDateTime?: string; // For time range appointment searches
  timeRangeEndDateTime?: string;   // For time range appointment searches
  selectedDataSource?: 'eds_mdm' | 'oim_cwow'; // For teammate username searches
}

const STORAGE_KEY = 'recent_patient_searches';
const MAX_RECENT_SEARCHES = 10;

@Injectable({
  providedIn: 'root'
})
export class RecentSearchesService {

  getRecentSearches(): RecentSearch[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error reading recent searches:', e);
    }
    return [];
  }

  addSearch(search: Omit<RecentSearch, 'id' | 'timestamp'>): void {
    const searches = this.getRecentSearches();

    // Create new search entry
    const newSearch: RecentSearch = {
      ...search,
      id: this.generateId(),
      timestamp: Date.now()
    };

    // Remove duplicate if exists (same query and type)
    const filtered = searches.filter(s =>
      !(s.type === search.type && s.query === search.query)
    );

    // Add to beginning and limit to max
    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);

    this.saveSearches(updated);
  }

  removeSearch(id: string): void {
    const searches = this.getRecentSearches();
    const filtered = searches.filter(s => s.id !== id);
    this.saveSearches(filtered);
  }

  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  private saveSearches(searches: RecentSearch[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
    } catch (e) {
      console.error('Error saving recent searches:', e);
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 9);
  }

  formatSearchDisplay(search: RecentSearch): string {
    switch (search.type) {
      case 'name':
        const name = [search.firstName, search.lastName].filter(Boolean).join(' ');
        return name || search.query;
      case 'mpi':
        return search.patientName ? `${search.patientName} (MPI: ${search.query})` : `MPI: ${search.query}`;
      case 'identifier':
        return `${search.identifierType?.toUpperCase() || 'ID'}: ${search.query}`;
      case 'teammate':
        return search.teammateName ? `${search.teammateName} (${search.query})` : `Facility: ${search.query}`;
      case 'teammate_username':
        const sourceLabel = search.selectedDataSource === 'eds_mdm' ? ' [EDS/MDM]' : ' [OIM/CWOW]';
        return search.teammateName
          ? `${search.teammateName} (${search.query})${sourceLabel}`
          : `Username: ${search.query}${sourceLabel}`;
      case 'teammate_facility':
        return `Facility: ${search.query}`;
      case 'provider':
        return search.providerName ? `${search.providerName} (NPI: ${search.query})` : `NPI: ${search.query}`;
      case 'facilityNumber':
        return search.excludeInactivePatients ? `Facility: ${search.query} | Exclude Inactive Patients: ${search.excludeInactivePatients}` : `Facility: ${search.query}`;
      case 'careteamlookup':
        return `MPI : ${search.query} Facility: ${search.facilityNumber}`;
      case 'appointment_mpi':
        return `Patient MPI: ${search.query}`;
      case 'appointment_facility':
        return `Facility: ${search.query}`;
      case 'appointment_eventId':
        return `Event ID: ${search.query}`;
      case 'appointment_timerange':
        if (search.timeRangeStartDateTime) {
          const start = new Date(search.timeRangeStartDateTime);
          return `Time Range: ${start.toLocaleString()} (+4 hrs)`;
        }
        return `Time Range: ${search.query}`;
      default:
        return search.query;
    }
  }

  getSearchTypeLabel(type: string): string {
    switch (type) {
      case 'name': return 'Name Search';
      case 'mpi': return 'MPI Search';
      case 'identifier': return 'Identifier Search';
      case 'teammate':
      case 'teammate_username':
      case 'teammate_facility':
        return 'Teammate Search';
      case 'provider': return 'Provider Search';
      case 'facilityNumber': return 'Facility Patient Search';
      case 'careteamlookup': return 'Care team lookup';
      case 'appointment_mpi':
      case 'appointment_facility':
      case 'appointment_eventId':
      case 'appointment_timerange':
        return 'Appointment Search';
      default: return 'Search';
    }
  }
}
